// CB:BEGIN pm-setup-modal
/**
 * PMSetupModal
 *
 * Blocking full-screen overlay shown on the PM dashboard when the user's
 * PropertyManagerProfile does not yet exist. Embeds all four onboarding
 * steps inline so the user never leaves the dashboard context.
 *
 * Steps (same as /pm-onboarding):
 *   0 — Platform Agreement
 *   1 — Add first property
 *   2 — Create first project (optional / skippable)
 *   3 — Company profile (submits everything, closes modal)
 *
 * Props:
 *   onComplete  — called after step 3; triggers fetchData() in the dashboard
 *   defaultCompanyName / defaultPhone / defaultCompanyAddress — pre-fill from signup
 */

import React, { useState, useCallback, useRef } from 'react';
import { useAuthFetch } from '../lib/useAuthFetch';
import { getApiUrl } from '../lib/getApiUrl';
import { ArrowRight, ChevronLeft, Building2, Download } from 'lucide-react';

const BRAND_GREEN = '#123c3b';
const TOTAL_STEPS = 3;
const PM_AGREEMENT_VERSION = '2';

const PM_AGREEMENT_TEXT = `COREBIDS PROPERTY MANAGER PLATFORM AGREEMENT
Version 2 | Effective Date: April 9, 2026

DEFINITIONS

"CoreBids" means CoreBids Inc., a Delaware C corporation, an online project marketplace and payment facilitator. "Platform" means the CoreBids web application, APIs, and related services. "Property Manager" or "PM" means the individual or business entity registering to submit and manage exterior repair and maintenance projects on behalf of properties they manage. "Property" means each physical asset submitted by the PM for project coordination. "Project" means a specific scope of exterior repair, replacement, or maintenance work submitted for contractor bidding. "Contractor" means an independent licensed contractor who receives bid requests through the Platform.

1. RELATIONSHIP OF PARTIES

PM is an independent business operator, not an employee, agent, partner, or joint venturer of CoreBids. By submitting a Property or Project, PM represents and warrants that: (a) PM has full legal authority to request, coordinate, and authorize exterior repair work on each submitted Property; (b) PM has obtained any required owner approvals, HOA authorizations, or lease provisions permitting the requested work; and (c) PM is not submitting work requests for properties where PM has no management authority.

2. PLATFORM PURPOSE AND SCOPE

The CoreBids Platform enables Property Managers to post exterior repair and maintenance projects and receive competitive bids from licensed contractors. CoreBids facilitates connection and payment processing. CoreBids does not guarantee work quality, workmanship, or code compliance; supervise construction; verify contractor licensing or insurance; or act as a general contractor. All warranty and performance obligations run directly between PM and Contractor under the project agreement.

3. USER AUTHORITY AND PROJECT SUBMISSION

Each time PM submits a Project, PM attests that PM has authority to commission and fund the requested work at that Property. PM agrees to provide accurate and complete information for each Property and Project, including correct address, accurate description of work, realistic deadlines (advisory only — not binding on Contractors), and relevant access and site information. Submitting materially false or misleading project information is a material breach.

CoreBids does not guarantee that any Project will receive bids, that any bid will meet PM's budget expectations, or that any Contractor will complete work by any requested deadline.

4. ACCURACY OF PROPERTY AND PROJECT INFORMATION

PM is responsible for the accuracy of all property data, project descriptions, photos, measurements, and reports submitted to the Platform. If measurement reports or third-party data are attached, such data is provided for contractor reference only — Contractors are responsible for their own field verification. CoreBids does not guarantee the accuracy of any measurements or reports.

5. DOCUMENT SHARING AND PERMISSIONS

PM may upload property-level documents (inspection reports, prior work orders, insurance claims, site photos, access instructions) to the Platform. By uploading documents, PM grants CoreBids a limited, non-exclusive license to store and display such documents to Contractors bidding on associated Projects. PM may not upload documents containing sensitive personal information unrelated to the project scope or documents subject to confidentiality restrictions or third-party intellectual property rights.

6. PLATFORM COMMUNICATIONS

PM agrees to receive operational communications from CoreBids including bid notifications, project status updates, milestone and payment confirmations, contractor messages, and platform notices by email to the address associated with PM's account. PM is responsible for keeping the account email current.

7. ACCEPTABLE USE AND ANTI-CIRCUMVENTION

PM may use the Platform only for lawful project coordination for properties PM legitimately manages. PM may not: submit false, duplicate, or placeholder projects; submit projects for properties PM has no authority to manage; use the Platform to solicit contractor contact information for use outside the Platform; or attempt to bypass the CoreBids payment system for any project originating on the Platform. Violation of the anti-circumvention provision is a material breach subject to account suspension and potential liability.

8. LIMITATION OF COREBIDS' ROLE

CoreBids makes no representations that the Platform will be error-free or uninterrupted. CoreBids is not a licensed contractor. PM's recourse for workmanship issues runs directly against the Contractor. Payments are processed by Stripe; CoreBids is not a bank or money transmitter and is not liable for payment processor actions, holds, or account restrictions.

9. DISPUTES AND LIABILITY

In disputes between PM and a Contractor, CoreBids may provide platform-level mediation assistance. CoreBids' determination regarding escrow funds is binding within the platform-defined scope. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, COREBIDS' TOTAL LIABILITY SHALL NOT EXCEED FEES PAID TO COREBIDS IN THE THREE MONTHS PRECEDING THE CLAIM. COREBIDS SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. PM agrees to indemnify CoreBids against claims arising out of PM's breach of this Agreement, misrepresentation of authority, or violation of applicable law.

10. GOVERNING LAW

This Agreement is governed by the laws of the State of Tennessee. Disputes not resolved informally shall be submitted to binding arbitration under AAA Commercial Rules in Nashville, Tennessee.

11. UPDATES

CoreBids may update this Agreement with reasonable notice by email or in-platform notification. Continued use after the effective date of any update constitutes acceptance. This Agreement, together with the CoreBids Terms of Service and Privacy Policy, constitutes the complete agreement between PM and CoreBids.

CoreBids Inc. · Nashville, TN · support@corebids.com`;

const PROJECT_TYPE_LABELS: Record<string, string> = {
  ROOF: 'Roof Repair / Replacement',
  SIDING: 'Siding Repair / Replacement',
  GUTTERS: 'Gutter Repair / Replacement',
  WINDOWS: 'Windows',
  DOORS: 'Doors',
  PAINTING: 'Exterior Painting',
  OTHER: 'Other Exterior Work',
};

const PROPERTY_TYPES = [
  { value: 'RESIDENTIAL', label: 'Single Family / Residential' },
  { value: 'MULTIFAMILY', label: 'Multi-Family / Apartments' },
  { value: 'HOA', label: 'HOA / Community' },
  { value: 'COMMERCIAL', label: 'Commercial / Retail / Office' },
  { value: 'OTHER', label: 'Other' },
];

const PORTFOLIO_SIZES = [
  { value: '1', label: '1 property' },
  { value: '2-5', label: '2–5 properties' },
  { value: '6-20', label: '6–20 properties' },
  { value: '21-50', label: '21–50 properties' },
  { value: '50+', label: '50+ properties' },
];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  border: '1.5px solid #e2e8f0',
  borderRadius: 10,
  fontSize: 15,
  color: '#0f172a',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#475569',
  marginBottom: 6,
};

const fieldWrap: React.CSSProperties = { marginBottom: 16 };

interface PMSetupModalProps {
  onComplete: () => void;
  defaultCompanyName?: string;
  defaultPhone?: string;
  defaultCompanyAddress?: string;
}

const PMSetupModal: React.FC<PMSetupModalProps> = ({
  onComplete,
  defaultCompanyName = '',
  defaultPhone = '',
  defaultCompanyAddress = '',
}) => {
  const authFetch = useAuthFetch();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdPropertyId, setCreatedPropertyId] = useState<string | null>(null);
  const [projectSkipped, setProjectSkipped] = useState(false);

  // Step 0 — agreement
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementTypedName, setAgreementTypedName] = useState('');
  const [agreementSignerPosition, setAgreementSignerPosition] = useState('');

  // Step 1 — property (collected locally; submitted with profile in step 2)
  const [property, setProperty] = useState({
    label: '',
    address1: '',
    city: '',
    state: '',
    zip: '',
    propertyType: 'RESIDENTIAL',
  });

  // Step 1 — address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<any[]>([]);
  const [addrShowSugg, setAddrShowSugg] = useState(false);
  const [addrSuggIdx, setAddrSuggIdx] = useState(-1);
  const addrIgnoreNext = useRef(false);
  const addrInputRef = useRef<HTMLInputElement>(null);

  const fetchAddrSugg = async (input: string) => {
    if (input.length < 3) { setAddrSuggestions([]); setAddrShowSugg(false); return; }
    try {
      const r = await fetch(getApiUrl(`/api/autocomplete?input=${encodeURIComponent(input)}`));
      if (r.ok) {
        const d = await r.json();
        if (d.predictions?.length) { setAddrSuggestions(d.predictions); setAddrShowSugg(true); setAddrSuggIdx(-1); }
        else { setAddrSuggestions([]); setAddrShowSugg(false); }
      }
    } catch { /**/ }
  };

  const selectAddrSugg = async (s: any) => {
    const street = (s.description || '').replace(/, USA$/, '').split(',')[0].trim();
    addrIgnoreNext.current = true;
    setProperty(p => ({ ...p, address1: street }));
    setAddrSuggestions([]); setAddrShowSugg(false);
    if (s.place_id) {
      try {
        const r = await fetch(getApiUrl(`/api/details?place_id=${encodeURIComponent(s.place_id)}`));
        if (r.ok) {
          const d = await r.json();
          const comps: any[] = d.result?.address_components || [];
          const get = (t: string) => comps.find((c: any) => c.types.includes(t));
          const addr1 = [get('street_number')?.long_name, get('route')?.short_name].filter(Boolean).join(' ') || street;
          setProperty(p => ({
            ...p,
            address1: addr1,
            city: get('locality')?.long_name || get('sublocality')?.long_name || '',
            state: get('administrative_area_level_1')?.short_name || '',
            zip: get('postal_code')?.long_name || '',
          }));
        }
      } catch { /**/ }
    }
    setTimeout(() => addrInputRef.current?.focus(), 0);
  };

  // Step 2 — profile (pre-filled from signup data)
  const [profile, setProfile] = useState({
    companyName: defaultCompanyName,
    contactName: '',
    phone: defaultPhone,
    portfolioSize: '',
    position: '',
  });

  // Step 3 — project
  const [project, setProject] = useState({
    projectType: 'ROOF',
    extraInfo: '',
    priority: 'NORMAL' as 'EMERGENCY' | 'HIGH' | 'NORMAL',
    requestedResponseBy: '',
  });

  const goBack = () => { setError(null); setStep(s => s - 1); };

  // ─── Step 0: agreement ───────────────────────────────────────────
  const handleAgreementSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreementTypedName.trim()) { setError('Please type your full legal name to sign.'); return; }
    if (!agreementSignerPosition.trim()) { setError('Please enter your position / title.'); return; }
    if (!agreementChecked) { setError('You must agree to the Platform Agreement to continue.'); return; }
    setError(null);
    setSaving(true);
    try {
      await authFetch(getApiUrl('/api/compliance/accept-pm-agreement'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: PM_AGREEMENT_VERSION,
          typedName: agreementTypedName.trim(),
          signerPosition: agreementSignerPosition.trim(),
        }),
      });
    } catch { /* non-fatal */ } finally {
      setSaving(false);
      setStep(1);
    }
  };

  // ─── Step 1: property (local only — submitted after profile in step 2) ───
  const handlePropertyAdvance = (e: React.FormEvent) => {
    e.preventDefault();
    if (!property.address1.trim()) { setError('Address is required.'); return; }
    if (!property.city.trim()) { setError('City is required.'); return; }
    if (!property.state.trim()) { setError('State is required.'); return; }
    if (!property.zip.trim()) { setError('ZIP code is required.'); return; }
    setError(null);
    setStep(2);
  };

  // ─── Step 2: project (optional — collected locally, submitted in step 3) ───
  const handleProjectAdvance = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setProjectSkipped(false);
    setStep(3);
  };

  // ─── Step 3: profile + stored property + optional project ────────
  const handleProfileSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile.companyName.trim()) { setError('Company name is required.'); return; }
    if (!profile.contactName.trim()) { setError('Your name is required.'); return; }
    if (!profile.phone.trim()) { setError('Phone number is required.'); return; }
    if (!profile.position.trim()) { setError('Position / title is required.'); return; }
    setError(null);
    setSaving(true);
    try {
      const resp = await authFetch(getApiUrl('/api/property-manager/onboard'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: profile.companyName.trim(),
          contactName: profile.contactName.trim(),
          phone: profile.phone.trim(),
          portfolioSize: profile.portfolioSize || undefined,
          position: profile.position.trim() || undefined,
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.error || 'Could not save profile.');
      }
      try { localStorage.setItem('cb.usertype', 'property_manager'); } catch {}
      try { localStorage.removeItem('cb.pm.signup'); } catch {}

      // Submit property if filled in (PM account exists after onboard)
      let propertyId: string | null = null;
      if (property.address1.trim()) {
        const propResp = await authFetch(getApiUrl('/api/property-manager/properties'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: property.label.trim() || undefined,
            address1: property.address1.trim(),
            city: property.city.trim(),
            state: property.state.trim().toUpperCase(),
            zip: property.zip.trim(),
            propertyType: property.propertyType,
          }),
        });
        if (propResp.ok) {
          const data = await propResp.json();
          propertyId = data?.property?.id || data?.id || null;
          setCreatedPropertyId(propertyId);
        }
      }

      // Submit project if user filled it in (not skipped) and we have a property
      if (!projectSkipped && propertyId) {
        const projBody: Record<string, any> = {
          propertyId,
          projectType: project.projectType,
          priority: project.priority,
        };
        if (project.extraInfo.trim()) projBody.extraInfo = project.extraInfo.trim();
        if (project.requestedResponseBy) projBody.requestedResponseBy = project.requestedResponseBy;
        await authFetch(getApiUrl('/api/property-manager/projects'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(projBody),
        }).catch(() => {});
      }

      onComplete();
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [authFetch, profile, property, project, projectSkipped, onComplete]);

  // ─── Shared UI helpers ───────────────────────────────────────────
  const ProgressBar = () => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>
        <span>Step {step + 1} of {TOTAL_STEPS}</span>
        <span style={{ color: BRAND_GREEN, fontWeight: 600 }}>
          {['Agreement', 'First Property', 'First Project', 'Company Profile'][step]}
        </span>
      </div>
      <div style={{ height: 5, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((step + 1) / TOTAL_STEPS) * 100}%`, background: `linear-gradient(90deg, ${BRAND_GREEN}, #1a6b69)`, borderRadius: 99, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );

  const BackBtn = ({ show }: { show: boolean }) => show ? (
    <button type="button" onClick={goBack} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', marginBottom: 8, padding: 0 }}>
      <ChevronLeft size={14} /> Back
    </button>
  ) : null;

  const ErrorBanner = () => error ? (
    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>{error}</div>
  ) : null;

  const PrimaryBtn = ({ label }: { label: string }) => (
    <button type="submit" disabled={saving} style={{ width: '100%', padding: '13px 24px', background: saving ? '#94a3b8' : BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 46 }}>
      {saving ? 'Saving…' : label}{!saving && <ArrowRight size={16} />}
    </button>
  );

  const SkipBtn = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <button type="button" onClick={onClick} style={{ display: 'block', width: '100%', marginTop: 10, padding: '8px', background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', textAlign: 'center' }}>{label}</button>
  );

  const SectionTitle = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 6, lineHeight: 1.2 }}>{title}</h2>
      <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>{subtitle}</p>
    </div>
  );

  // ─── Step renderers ──────────────────────────────────────────────

  const renderStep0 = () => (
    <>
      <ProgressBar />
      <SectionTitle title="Platform Agreement" subtitle="Please read and sign the Property Manager Platform Agreement before continuing." />
      <ErrorBanner />
      <form onSubmit={handleAgreementSubmit}>
        <div style={fieldWrap}>
          {/* Download / view button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button
              type="button"
              onClick={async () => {
                try {
                  const resp = await authFetch(getApiUrl('/api/agreements/pm-platform/pdf'));
                  if (!resp.ok) throw new Error('Failed to fetch PDF');
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank', 'noopener');
                  setTimeout(() => URL.revokeObjectURL(url), 30000);
                } catch {
                  window.open(getApiUrl('/api/agreements/pm-platform/pdf'), '_blank', 'noopener');
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid #e2e8f0', borderRadius: 7, padding: '5px 11px', fontSize: 12, color: '#475569', cursor: 'pointer', fontWeight: 500 }}
            >
              <Download size={12} /> View / Download
            </button>
          </div>
          <div style={{ maxHeight: 260, overflow: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', fontSize: 12.5, color: '#475569', lineHeight: 1.75, background: '#f8fafc', whiteSpace: 'pre-wrap' }}>
            {PM_AGREEMENT_TEXT}
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Full legal name <span style={{ color: '#dc2626' }}>*</span></label>
          <input style={inputStyle} placeholder="e.g. Jordan Martinez" value={agreementTypedName} onChange={e => setAgreementTypedName(e.target.value)} autoFocus />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Position / Title <span style={{ color: '#dc2626' }}>*</span></label>
          <input style={inputStyle} placeholder="e.g. CEO, Operations Manager, Property Manager" value={agreementSignerPosition} onChange={e => setAgreementSignerPosition(e.target.value)} />
        </div>
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <input id="modal-pm-agree" type="checkbox" checked={agreementChecked} onChange={e => setAgreementChecked(e.target.checked)} style={{ marginTop: 2, flexShrink: 0, cursor: 'pointer', width: 16, height: 16 }} />
          <label htmlFor="modal-pm-agree" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer', fontWeight: 400, fontSize: 14, color: '#1f2937' }}>
            I have read and agree to the Property Manager Platform Agreement <span style={{ color: '#dc2626' }}>*</span>
          </label>
        </div>
        <PrimaryBtn label="Continue" />
      </form>
    </>
  );

  const renderStep1 = () => (
    <>
      <BackBtn show />
      <ProgressBar />
      <SectionTitle title="Add your first property" subtitle="Start with one — you can add more from your dashboard at any time." />
      <ErrorBanner />
      <form onSubmit={handlePropertyAdvance} autoComplete="off">
        <div style={fieldWrap}>
          <label style={labelStyle}>Property Name <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
          <input style={inputStyle} placeholder="e.g. Belmont Townhomes — Bldg C" value={property.label} onChange={e => setProperty(p => ({ ...p, label: e.target.value }))} />
        </div>

        {/* Street address with Google autocomplete */}
        <div style={{ ...fieldWrap, position: 'relative' }}>
          <label style={labelStyle}>Street Address <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            ref={addrInputRef}
            style={inputStyle}
            placeholder="Start typing an address…"
            value={property.address1}
            autoComplete="off"
            onChange={e => {
              setProperty(p => ({ ...p, address1: e.target.value, city: '', state: '', zip: '' }));
              if (addrIgnoreNext.current) { addrIgnoreNext.current = false; return; }
              fetchAddrSugg(e.target.value);
            }}
            onKeyDown={e => {
              if (!addrShowSugg) return;
              if (e.key === 'ArrowDown') { e.preventDefault(); setAddrSuggIdx(i => Math.min(i + 1, addrSuggestions.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setAddrSuggIdx(i => Math.max(i - 1, -1)); }
              else if (e.key === 'Enter' && addrSuggIdx >= 0) { e.preventDefault(); selectAddrSugg(addrSuggestions[addrSuggIdx]); }
              else if (e.key === 'Escape') setAddrShowSugg(false);
            }}
            onBlur={() => setTimeout(() => setAddrShowSugg(false), 150)}
          />
          {addrShowSugg && addrSuggestions.length > 0 && (
            <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, marginTop: 2, padding: 0, listStyle: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
              {addrSuggestions.map((s, i) => (
                <li
                  key={s.place_id || i}
                  onMouseDown={() => selectAddrSugg(s)}
                  style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', background: i === addrSuggIdx ? '#f0fdf4' : '#fff', color: '#111827', borderBottom: i < addrSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none' }}
                >
                  {(s.description || '').replace(/, USA$/, '')}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* City / State / ZIP — auto-filled by Google, read-only */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'City', key: 'city', ph: 'Auto-filled' },
            { label: 'State', key: 'state', ph: 'Auto' },
            { label: 'ZIP', key: 'zip', ph: 'Auto-filled' },
          ].map(f => (
            <div key={f.key}>
              <label style={labelStyle}>{f.label} <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                readOnly
                style={{ ...inputStyle, background: property[f.key as keyof typeof property] ? '#f0fdf4' : '#f8fafc', color: property[f.key as keyof typeof property] ? '#1a6b4a' : '#94a3b8', cursor: 'default' }}
                placeholder={f.ph}
                value={property[f.key as keyof typeof property] as string}
              />
            </div>
          ))}
        </div>
        {!property.city && property.address1.length > 2 && (
          <p style={{ fontSize: 12, color: '#64748b', marginTop: -10, marginBottom: 12 }}>Select an address from the dropdown to auto-fill city, state, and ZIP.</p>
        )}

        <div style={fieldWrap}>
          <label style={labelStyle}>Property Type</label>
          <select style={{ ...inputStyle, appearance: 'auto' }} value={property.propertyType} onChange={e => setProperty(p => ({ ...p, propertyType: e.target.value }))}>
            {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <PrimaryBtn label="Save and Continue" />
        <SkipBtn label="Skip — I'll add properties from the dashboard" onClick={() => { setError(null); setStep(2); }} />
      </form>
    </>
  );

  const renderStep2 = () => (
    <>
      <BackBtn show />
      <ProgressBar />
      <SectionTitle title="Create your first project" subtitle="Try CoreBids on one repair job. You'll receive bids after submitting." />
      {!property.address1.trim() && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#854d0e' }}>
          No property added — you can still fill this in, but the project won't be linked to an address.
        </div>
      )}
      <ErrorBanner />
      <form onSubmit={handleProjectAdvance}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Type of Work <span style={{ color: '#dc2626' }}>*</span></label>
          <select style={{ ...inputStyle, appearance: 'auto' }} value={project.projectType} onChange={e => setProject(p => ({ ...p, projectType: e.target.value }))}>
            {Object.entries(PROJECT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Brief Description <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
          <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} placeholder="e.g. Active leak on south face near HVAC unit." value={project.extraInfo} onChange={e => setProject(p => ({ ...p, extraInfo: e.target.value }))} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Priority</label>
            <select style={{ ...inputStyle, appearance: 'auto' }} value={project.priority} onChange={e => setProject(p => ({ ...p, priority: e.target.value as any }))}>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
              <option value="EMERGENCY">Emergency</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Respond By <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
            <input style={inputStyle} type="date" value={project.requestedResponseBy} onChange={e => setProject(p => ({ ...p, requestedResponseBy: e.target.value }))} min={new Date().toISOString().split('T')[0]} />
          </div>
        </div>
        <PrimaryBtn label="Continue" />
        <SkipBtn label="Skip — I'll create projects from the dashboard" onClick={() => { setError(null); setProjectSkipped(true); setStep(3); }} />
      </form>
    </>
  );

  const renderStep3 = () => (
    <>
      <BackBtn show />
      <ProgressBar />
      <SectionTitle title="Set up your company profile" subtitle="This takes about a minute. You can update everything later." />
      <ErrorBanner />
      <form onSubmit={handleProfileSubmit} autoComplete="on">
        <div style={fieldWrap}>
          <label style={labelStyle}>Company Name <span style={{ color: '#dc2626' }}>*</span></label>
          <input style={inputStyle} placeholder="e.g. Pinnacle Property Management" value={profile.companyName} onChange={e => setProfile(p => ({ ...p, companyName: e.target.value }))} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Your Name <span style={{ color: '#dc2626' }}>*</span></label>
          <input style={inputStyle} placeholder="e.g. Jordan Martinez" value={profile.contactName} onChange={e => setProfile(p => ({ ...p, contactName: e.target.value }))} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Position / Title <span style={{ color: '#dc2626' }}>*</span></label>
          <input style={inputStyle} placeholder="e.g. Property Manager, Facilities Director" value={profile.position} onChange={e => setProfile(p => ({ ...p, position: e.target.value }))} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Phone <span style={{ color: '#dc2626' }}>*</span></label>
          <input style={inputStyle} type="tel" placeholder="e.g. 615-555-0100" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Number of Properties <span style={{ color: '#94a3b8', fontWeight: 400 }}>(roughly)</span></label>
          <select style={{ ...inputStyle, appearance: 'auto' }} value={profile.portfolioSize} onChange={e => setProfile(p => ({ ...p, portfolioSize: e.target.value }))}>
            <option value="">Select…</option>
            {PORTFOLIO_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <PrimaryBtn label="Finish Setup" />
        <p style={{ marginTop: 12, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>No credit card required. Account is free.</p>
      </form>
    </>
  );

  return (
    // Full-screen overlay — pointer-events block everything behind
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        padding: '40px 16px 60px',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 20,
          border: '1.5px solid #e2e8f0',
          padding: '40px 44px 36px',
          maxWidth: 680,
          width: '100%',
          boxShadow: '0 8px 48px rgba(0,0,0,0.18)',
          position: 'relative',
        }}
        // Prevent clicks on the card from bubbling to the backdrop
        onClick={e => e.stopPropagation()}
      >
        {/* Header badge */}
        {step < 3 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <div style={{ display: 'inline-flex', padding: 8, background: '#e6f5ef', borderRadius: 10 }}>
              <Building2 size={20} style={{ color: BRAND_GREEN }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: BRAND_GREEN }}>Finish Account Setup</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>Complete this to unlock your dashboard</div>
            </div>
          </div>
        )}

        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </div>
  );
};

export default PMSetupModal;
// CB:END pm-setup-modal
